import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Dog, Cat, Activity, ChevronRight, Utensils, Sparkles, Zap, RotateCcw, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Badge } from '../ui/Badge';
export function InteractiveNutritionCalculator() {
  const [step, setStep] = useState(1);
  const [petType, setPetType] = useState<'dog' | 'cat' | null>(null);
  const [weight, setWeight] = useState(10);
  const [activity, setActivity] = useState<'low' | 'medium' | 'high'>('medium');
  const [isCalculating, setIsCalculating] = useState(false);
  const [showResult, setShowResult] = useState(false);
  // Real RER (Resting Energy Requirement) Calculation
  // Formula: 70 * (weight in kg ^ 0.75)
  const calculateCalories = () => {
    setIsCalculating(true);
    // Simulate API/Calculation delay for UX
    setTimeout(() => {
      setIsCalculating(false);
      setShowResult(true);
    }, 1500);
  };
  const getDailyCalories = () => {
    const rer = 70 * Math.pow(weight, 0.75);
    let multiplier = 1.0;
    if (petType === 'dog') {
      if (activity === 'low') multiplier = 1.4;else if (activity === 'medium') multiplier = 1.6;else if (activity === 'high') multiplier = 2.0; // Working dog
    } else {
      // Cat
      if (activity === 'low') multiplier = 1.0;else if (activity === 'medium') multiplier = 1.2;else if (activity === 'high') multiplier = 1.4;
    }
    return Math.round(rer * multiplier);
  };
  const getMonthlyCost = (calories: number) => {
    // Mock cost per calorie (approx 0.005 euros per kcal for premium food)
    return Math.round(calories * 30 * 0.005);
  };
  const resetCalculator = () => {
    setShowResult(false);
    setStep(1);
    setPetType(null);
    setWeight(10);
    setActivity('medium');
  };
  const dailyCalories = getDailyCalories();
  const monthlyCost = getMonthlyCost(dailyCalories);
  return <section className="py-24 bg-gradient-to-br from-primary-900 to-charcoal-900 text-white relative overflow-hidden min-h-[800px] flex items-center" data-id="element-2408">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" data-id="element-2409" />
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-primary-500/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-2410" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-purple-500/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/3 pointer-events-none" data-id="element-2411" />

      <div className="container-custom max-w-6xl mx-auto relative z-10" data-id="element-2412">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-2413">
          {/* Left: Narrative */}
          <div className="space-y-8" data-id="element-2414">
            <EnhancedScrollReveal data-id="element-2415">
              <Badge className="bg-white/10 text-white border-white/20 mb-6 backdrop-blur-sm" data-id="element-2416">
                <Calculator className="w-3 h-3 mr-1" data-id="element-2417" /> Algoritmo Veterinario
              </Badge>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6 leading-tight" data-id="element-2418">
                Nutrición de precisión, <br data-id="element-2419" />
                <span className="text-primary-400" data-id="element-2420">cero adivinanzas</span>
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed max-w-lg font-light" data-id="element-2421">
                Olvídate de las tazas medidoras genéricas. Calculamos las
                necesidades calóricas exactas de tu mascota basándonos en su
                biología única (fórmula RER veterinaria).
              </p>
            </EnhancedScrollReveal>

            <EnhancedScrollReveal delay={0.2} data-id="element-2422">
              <div className="grid grid-cols-2 gap-6" data-id="element-2423">
                {[{
                label: 'Precisión Calórica',
                val: '99.8%'
              }, {
                label: 'Mascotas Analizadas',
                val: '50k+'
              }].map((stat, i) => <div key={i} className="bg-white/5 border border-white/10 p-4 rounded-2xl backdrop-blur-sm" data-id="element-2424">
                    <div className="text-2xl font-bold text-white mb-1" data-id="element-2425">
                      {stat.val}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider" data-id="element-2426">
                      {stat.label}
                    </div>
                  </div>)}
              </div>
            </EnhancedScrollReveal>
          </div>

          {/* Right: Calculator Wizard */}
          <EnhancedScrollReveal delay={0.3} className="w-full" data-id="element-2427">
            <div className="bg-white rounded-[3rem] p-8 md:p-12 shadow-2xl text-charcoal-900 relative overflow-hidden min-h-[550px] flex flex-col" data-id="element-2428">
              {!showResult ? <>
                  {/* Progress Header */}
                  <div className="flex justify-between items-center mb-12" data-id="element-2429">
                    <div className="flex gap-2" data-id="element-2430">
                      {[1, 2, 3].map(i => <motion.div key={i} initial={false} animate={{
                    backgroundColor: i <= step ? '#2D4A3E' : '#E5E7EB',
                    scale: i === step ? 1.2 : 1
                  }} className="w-3 h-3 rounded-full" data-id="element-2431" />)}
                    </div>
                    <div className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2432">
                      Paso {step} de 3
                    </div>
                  </div>

                  <AnimatePresence mode="wait" data-id="element-2433">
                    {step === 1 && <motion.div key="step1" initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.3
                }} className="flex-1 flex flex-col" data-id="element-2434">
                        <h3 className="text-3xl font-serif font-bold text-center mb-8" data-id="element-2435">
                          ¿Quién es el comensal?
                        </h3>
                        <div className="grid grid-cols-2 gap-6 mb-8" data-id="element-2436">
                          <button onClick={() => {
                      setPetType('dog');
                      setStep(2);
                    }} className={`aspect-square rounded-3xl border-2 flex flex-col items-center justify-center gap-4 transition-all hover:border-primary-500 hover:bg-primary-50 ${petType === 'dog' ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-200' : 'border-gray-100'}`} data-id="element-2437">
                            <Dog className={`w-16 h-16 ${petType === 'dog' ? 'text-primary-600' : 'text-gray-400'}`} data-id="element-2438" />
                            <span className="font-bold text-xl" data-id="element-2439">Perro</span>
                          </button>
                          <button onClick={() => {
                      setPetType('cat');
                      setStep(2);
                    }} className={`aspect-square rounded-3xl border-2 flex flex-col items-center justify-center gap-4 transition-all hover:border-blue-500 hover:bg-blue-50 ${petType === 'cat' ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-100'}`} data-id="element-2440">
                            <Cat className={`w-16 h-16 ${petType === 'cat' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2441" />
                            <span className="font-bold text-xl" data-id="element-2442">Gato</span>
                          </button>
                        </div>
                      </motion.div>}

                    {step === 2 && <motion.div key="step2" initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.3
                }} className="flex-1 flex flex-col" data-id="element-2443">
                        <h3 className="text-3xl font-serif font-bold text-center mb-12" data-id="element-2444">
                          ¿Cuánto pesa?
                        </h3>

                        <div className="flex-1 flex flex-col justify-center items-center mb-12" data-id="element-2445">
                          <div className="text-6xl font-bold text-primary-600 mb-4 font-serif" data-id="element-2446">
                            {weight}{' '}
                            <span className="text-2xl text-gray-400" data-id="element-2447">kg</span>
                          </div>

                          <input type="range" min="1" max="60" value={weight} onChange={e => setWeight(parseInt(e.target.value))} className="w-full h-3 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-primary-600" data-id="element-2448" />
                          <div className="flex justify-between w-full text-xs text-gray-400 mt-4 font-bold uppercase tracking-wider" data-id="element-2449">
                            <span data-id="element-2450">Pequeño</span>
                            <span data-id="element-2451">Mediano</span>
                            <span data-id="element-2452">Grande</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4" data-id="element-2453">
                          <Button variant="outline" onClick={() => setStep(1)} size="xl" className="rounded-full" data-id="element-2454">
                            Atrás
                          </Button>
                          <Button onClick={() => setStep(3)} fullWidth size="xl" className="rounded-full" rightIcon={<ChevronRight className="w-5 h-5" data-id="element-2456" />} data-id="element-2455">
                            Siguiente
                          </Button>
                        </div>
                      </motion.div>}

                    {step === 3 && <motion.div key="step3" initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: -20
                }} transition={{
                  duration: 0.3
                }} className="flex-1 flex flex-col" data-id="element-2457">
                        <h3 className="text-3xl font-serif font-bold text-center mb-8" data-id="element-2458">
                          Nivel de Actividad
                        </h3>

                        <div className="space-y-4 mb-8" data-id="element-2459">
                          {[{
                      id: 'low',
                      label: 'Sofá Potato',
                      icon: Sparkles,
                      desc: 'Paseos cortos, siestas largas'
                    }, {
                      id: 'medium',
                      label: 'Explorador Urbano',
                      icon: Activity,
                      desc: '1-2h de actividad diaria'
                    }, {
                      id: 'high',
                      label: 'Atleta Olímpico',
                      icon: Zap,
                      desc: 'Entrenamiento intenso, correr'
                    }].map(level => <button key={level.id} onClick={() => setActivity(level.id as any)} className={`w-full p-5 rounded-2xl border-2 text-left transition-all flex items-center gap-4 ${activity === level.id ? 'border-primary-600 bg-primary-50 ring-1 ring-primary-200' : 'border-gray-100 hover:border-gray-200'}`} data-id="element-2460">
                              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${activity === level.id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-2461">
                                <level.icon className="w-6 h-6" data-id="element-2462" />
                              </div>
                              <div data-id="element-2463">
                                <div className="font-bold text-lg text-charcoal-900" data-id="element-2464">
                                  {level.label}
                                </div>
                                <div className="text-sm text-charcoal-500" data-id="element-2465">
                                  {level.desc}
                                </div>
                              </div>
                            </button>)}
                        </div>

                        <div className="grid grid-cols-2 gap-4" data-id="element-2466">
                          <Button variant="outline" onClick={() => setStep(2)} size="xl" className="rounded-full" data-id="element-2467">
                            Atrás
                          </Button>
                          <Button onClick={calculateCalories} fullWidth size="xl" className="rounded-full" pulse={!isCalculating} isLoading={isCalculating} loadingText="Calculando..." data-id="element-2468">
                            Calcular Plan
                          </Button>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </> : <motion.div key="result" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              type: 'spring',
              duration: 0.6
            }} className="flex-1 flex flex-col items-center text-center" data-id="element-2469">
                  <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6 animate-bounce-slow" data-id="element-2470">
                    <Utensils className="w-10 h-10" data-id="element-2471" />
                  </div>

                  <h3 className="text-3xl font-serif font-bold text-charcoal-900 mb-2" data-id="element-2472">
                    Plan Personalizado
                  </h3>
                  <p className="text-charcoal-500 mb-8" data-id="element-2473">
                    Para tu {petType === 'dog' ? 'perro' : 'gato'} de {weight}kg
                    (
                    {activity === 'low' ? 'Baja' : activity === 'medium' ? 'Media' : 'Alta'}{' '}
                    actividad)
                  </p>

                  <div className="w-full bg-primary-50 rounded-3xl p-6 border border-primary-100 mb-8" data-id="element-2474">
                    <div className="grid grid-cols-2 gap-8 divide-x divide-primary-200" data-id="element-2475">
                      <div data-id="element-2476">
                        <div className="text-xs font-bold text-primary-400 uppercase tracking-wider mb-1" data-id="element-2477">
                          Calorías Diarias
                        </div>
                        <div className="text-4xl font-bold text-primary-700 font-serif" data-id="element-2478">
                          {dailyCalories}
                        </div>
                        <div className="text-xs text-primary-500" data-id="element-2479">kcal</div>
                      </div>
                      <div data-id="element-2480">
                        <div className="text-xs font-bold text-primary-400 uppercase tracking-wider mb-1" data-id="element-2481">
                          Costo Estimado
                        </div>
                        <div className="text-4xl font-bold text-primary-700 font-serif" data-id="element-2482">
                          {monthlyCost}€
                        </div>
                        <div className="text-xs text-primary-500" data-id="element-2483">/mes</div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 w-full mt-auto" data-id="element-2484">
                    <Button size="xl" className="w-full rounded-full shadow-xl" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-2486" />} data-id="element-2485">
                      Ver Recetas Recomendadas
                    </Button>
                    <button onClick={resetCalculator} className="text-sm font-bold text-gray-400 hover:text-charcoal-600 transition-colors flex items-center justify-center gap-2 py-2" data-id="element-2487">
                      <RotateCcw className="w-4 h-4" data-id="element-2488" /> Empezar de nuevo
                    </button>
                  </div>
                </motion.div>}
            </div>
          </EnhancedScrollReveal>
        </div>
      </div>
    </section>;
}